import { useFetchWebsiteHeaderSettings } from '@apps/guest/packages/layout-engine/layouts/hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { useFetchWebsitePageListData } from '@shared/hooks/useWebsitePages';

type UsePrefetchSsrQueriesArgs = Readonly<{
  eventHandle: string;
}>;

/**
 * Prefetches layout-level queries that would otherwise be blocked by the component tree.
 *
 * Problem: GuestSiteBody.tsx conditionally renders the layout only when `event` is defined
 * (`event && layout ? <LayoutProvider> ... </LayoutProvider> : null`). Layout queries live
 * inside that layout tree, so the client can't discover them until EventWebsiteGuest
 * resolves, creating a waterfall.
 *
 * Solution: Call these data-fetching hooks here — above the conditional render — so they
 * fire concurrently with EventWebsiteGuest. Apollo caches the results; when the layout
 * components mount after `event` resolves, they read from cache instead of making new
 * network requests.
 *
 * Scope: layout-level only (header settings, page list). Page-content queries intentionally
 * fire organically when their widget mounts — prefetching them here wastes bandwidth on
 * layouts that render one page at a time (Brannan classic) and already isn't needed for
 * priority: each page-content hook sets `batchMode: 'off'` via `useIsActiveTarget` when
 * its route is the deep-linked target.
 */
export const usePrefetchSsrQueries = (args: UsePrefetchSsrQueriesArgs) => {
  const { eventHandle } = args;

  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();
  const skip = !isGuestSiteSsrEnabled;

  useFetchWebsiteHeaderSettings(eventHandle, skip);
  useFetchWebsitePageListData(eventHandle, skip);
};
